import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/page-layout';
import { PageLoader } from '../components/page-loader';
import { transferSessionAnswers } from '../services/backend.service';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import analyticsService from '../services/analytics.service';
import { FormShell } from '../components/onyx/FormShell';
import { FormActions } from '../components/onyx/Field';
import { Button } from '../components/onyx/Button';

export const PreScreenTransferPage = () => {
  const { callApi, isAuthenticated, isAuthLoading } = useAuthenticatedApi();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [transferring, setTransferring] = useState(true);

  useEffect(() => {
    const handleTransfer = async () => {
      try {
        if (isAuthLoading) return;

        if (!isAuthenticated) {
          // User is not authenticated, redirect to home
          navigate('/');
          return;
        }

        const transferSessionKey = localStorage.getItem('transfer_session_key');
        
        if (!transferSessionKey) {
          // No session to transfer, redirect to dashboard
          navigate('/dashboard');
          return;
        }

        console.log('Transferring session data...', { transferSessionKey });
        
        // Use callApi with automatic retry on 403 errors
        // This handles the race condition where the user just signed up
        // and the Post-User-Registration Action hasn't completed yet
        const { data, error } = await callApi(
          (token) => transferSessionAnswers(token, transferSessionKey)
        );
        
        if (error) {
          console.error('Failed to transfer session data:', error);
          setError('Failed to transfer your answers. Please try again.');
          setTransferring(false);
          return;
        }

        console.log('Session transfer successful:', data);
        
        // Clear the stored session keys
        localStorage.removeItem('transfer_session_key');
        localStorage.removeItem('transfer_question_group');
        localStorage.removeItem('prescreen_session_key');
        
        // Track successful transfer
        analyticsService.trackOnboarding('pre_screen_session_transferred', 'data_migration', {
          transferred_answers: data?.transferred_answers,
          next_step: data?.next_step
        });
        
        // Navigate to the appropriate next step based on backend response
        if (data?.next_step === 'PENDING_APPROVAL') {
          navigate('/pending-approval');
        } else if (data?.next_step === 'CONSENT') {
          navigate('/consent');
        } else if (data?.next_step === 'VERIFY') {
          navigate('/verify');
        } else {
          // Fallback to dashboard
          navigate('/dashboard');
        }
        
      } catch (error) {
        console.error('Transfer error:', error);
        setError('Failed to transfer your answers. Please try again.');
        setTransferring(false);
      }
    };

    handleTransfer();
  }, [isAuthenticated, isAuthLoading, callApi, navigate]);

  if (isAuthLoading || transferring) {
    return (
      <PageLayout>
        <div className="content-layout">
          <FormShell>
            <h1 id="page-title" className="content__title">
              Transferring Your Answers...
            </h1>
            <p className="content__body">
              Please wait while we securely transfer your pre-screen answers to your account.
            </p>
            <PageLoader />
          </FormShell>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="content-layout">
          <FormShell>
            <h1 id="page-title" className="content__title">
              Transfer Error
            </h1>
            <p className="content__body">
              {error}
            </p>
            <FormActions>
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate('/dashboard')}
              >
                Continue to Dashboard
              </Button>
            </FormActions>
          </FormShell>
        </div>
      </PageLayout>
    );
  }

  return null; // Should not reach here
}; 